import { skipToken, useQuery } from '@tanstack/react-query';

import { getProjectCredits } from '@/features/credits';
import useAuth from '@/hooks/useAuth';
import useLibraryItemQuery from '@/hooks/queries/useLibraryItemQuery';

interface UsePresetCreditsCostParams {
  slug: string;
  isEnabled: boolean;
}

/**
 * Credits cost of generating one preset, looked up by slug.
 *
 * Deliberately NOT `useCredits`: that reads the store's single value — the
 * *actively selected* preset's cost — while feed slides need several presets'
 * costs alive at once (mid-swipe two bars show two different prices). Reads
 * the same cache keys `loadActivePreset` and `useCreditsSync` populate
 * (`['libraryItemInfo', slug]` → `['projectCredits', flowId]`), so the active
 * slide costs zero extra requests and only `isEnabled` neighbors prefetch.
 * `getProjectCredits` is an authorized call — logged out, the lookup stays
 * off and `creditsCost` stays undefined. If the feed experiment wins, fold
 * this back into a per-preset `useCredits` (see docs/experiments/preset-feed.md).
 */
const usePresetCreditsCost = ({
  slug,
  isEnabled,
}: UsePresetCreditsCostParams): {
  creditsCost: number | null | undefined;
  isCreditsCostLoading: boolean;
} => {
  const { isAuthorized } = useAuth();
  const isLookupEnabled = isEnabled && isAuthorized;
  const { data: preset } = useLibraryItemQuery(
    isLookupEnabled ? slug : undefined
  );
  const flowId = preset?.flowId;
  const { data: creditsCost } = useQuery({
    queryKey: ['projectCredits', flowId],
    queryFn:
      isLookupEnabled && flowId
        ? () => getProjectCredits({ webhookId: flowId })
        : skipToken,
  });
  const isCreditsCostLoading =
    isLookupEnabled && preset !== null && creditsCost === undefined;

  return { creditsCost, isCreditsCostLoading };
};

export default usePresetCreditsCost;
